import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useUser } from '../../context/UserContext';
import { sampleCodeSnippets, difficultyColors } from '../../data/sampleCodeSnippets';
import './CodeTikTok.css';

function CodeTikTok() {
  const { user } = useUser();
  const [snippets, setSnippets] = useState(sampleCodeSnippets);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const containerRef = useRef(null);

  const currentSnippet = snippets[currentIndex];

  const goToNext = useCallback(() => {
    if (currentIndex < snippets.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setShowComments(false);
    }
  }, [currentIndex, snippets.length]);

  const goToPrevious = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setShowComments(false);
    }
  }, [currentIndex]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        goToPrevious();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        goToNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToNext, goToPrevious]);

  // Handle touch swipe
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientY);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientY);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 75) {
      // Swiped up
      goToNext();
    }

    if (touchStart - touchEnd < -75) {
      // Swiped down
      goToPrevious();
    }
  };

  // Handle mouse wheel
  const handleWheel = (e) => {
    e.preventDefault();
    if (e.deltaY > 0) {
      goToNext();
    } else if (e.deltaY < 0) {
      goToPrevious();
    }
  };

  const handleLike = () => {
    setSnippets(snippets.map((s, i) => {
      if (i === currentIndex) {
        return {
          ...s,
          userLiked: !s.userLiked,
          likes: s.userLiked ? s.likes - 1 : s.likes + 1
        };
      }
      return s;
    }));
  };

  const handleComment = () => {
    if (!commentText.trim()) return;

    setSnippets(snippets.map((s, i) => {
      if (i === currentIndex) {
        return {
          ...s,
          comments: s.comments + 1,
          commentsList: [
            ...(s.commentsList || []),
            {
              id: Date.now(),
              username: user?.name || 'Anonymous',
              text: commentText,
              createdAt: new Date()
            }
          ]
        };
      }
      return s;
    }));

    setCommentText('');
  };

  const handleRemix = () => {
    alert('🎨 Remix feature coming soon! You\'ll be able to edit and share your own version!');
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: currentSnippet.title,
        text: `Check out this coding tip: ${currentSnippet.description}`,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('📋 Link copied to clipboard!');
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(currentSnippet.code);
    alert('✅ Code copied to clipboard!');
  };

  const runCode = () => {
    alert('▶️ Code execution coming soon! You\'ll be able to run code right here!');
  };

  return (
    <div 
      className="code-tiktok"
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onWheel={handleWheel}
    >
      {/* Navigation Hints */}
      <div className="navigation-hints">
        <div className="hint">
          <span className="arrow">↑</span>
          <span>Swipe up or press ↑</span>
        </div>
        <div className="hint">
          <span className="arrow">↓</span>
          <span>Swipe down or press ↓</span>
        </div>
      </div>

      {/* Main Snippet Card */}
      <div className="snippet-card" style={{ transform: `translateY(0)` }}>
        {/* Background gradient based on difficulty */}
        <div 
          className="snippet-background"
          style={{
            background: `linear-gradient(135deg, ${difficultyColors[currentSnippet.difficulty]}20 0%, ${difficultyColors[currentSnippet.difficulty]}40 100%)`
          }}
        />

        {/* Header */}
        <div className="snippet-header">
          <div className="author-info">
            <span className="author-avatar">{currentSnippet.authorAvatar}</span>
            <div className="author-details">
              <h3>{currentSnippet.authorName}</h3>
              <p>{currentSnippet.duration} • {currentSnippet.topic}</p>
            </div>
          </div>
          <button className="follow-btn">+ Follow</button>
        </div>

        {/* Title & Description */}
        <div className="snippet-content">
          <h1>{currentSnippet.title}</h1>
          <p className="description">{currentSnippet.description}</p>

          {/* Hashtags */}
          <div className="hashtags">
            {currentSnippet.hashtags.map((tag, index) => (
              <span key={index} className="hashtag">{tag}</span>
            ))}
          </div>

          {/* Difficulty Badge */}
          <div 
            className="difficulty-badge"
            style={{ 
              background: difficultyColors[currentSnippet.difficulty],
              color: 'white'
            }}
          >
            {currentSnippet.difficulty.toUpperCase()}
          </div>

          {/* Trending Badge */}
          {currentSnippet.trending && (
            <div className="trending-badge">
              🔥 TRENDING
            </div>
          )}
        </div>

        {/* Code Block */}
        <div className="code-block">
          <div className="code-header">
            <span>📝 Code</span>
            <button onClick={copyCode} className="copy-btn">
              📋 Copy
            </button>
          </div>
          <pre><code>{currentSnippet.code}</code></pre>
          <button onClick={runCode} className="run-btn">
            ▶️ Run Code
          </button>
        </div>

        {/* Challenge */}
        <div className="challenge-box">
          <h4>🎯 Challenge:</h4>
          <p>{currentSnippet.challenge}</p>
        </div>

        {/* Action Buttons (Right Side) */}
        <div className="action-buttons">
          <button 
            className={`action-btn ${currentSnippet.userLiked ? 'liked' : ''}`}
            onClick={handleLike}
          >
            <span className="icon">{currentSnippet.userLiked ? '❤️' : '🤍'}</span>
            <span className="count">{formatCount(currentSnippet.likes)}</span>
          </button>

          <button 
            className="action-btn"
            onClick={() => setShowComments(!showComments)}
          >
            <span className="icon">💬</span>
            <span className="count">{formatCount(currentSnippet.comments)}</span>
          </button>

          <button className="action-btn" onClick={handleRemix}>
            <span className="icon">🎨</span>
            <span className="count">{formatCount(currentSnippet.remixes)}</span>
          </button>

          <button className="action-btn" onClick={handleShare}>
            <span className="icon">📤</span>
            <span className="count">Share</span>
          </button>

          <button className="action-btn view-count">
            <span className="icon">👁️</span>
            <span className="count">{formatCount(currentSnippet.views)}</span>
          </button>
        </div>

        {/* Progress Indicator */}
        <div className="progress-indicator">
          <span>{currentIndex + 1}</span>
          <span>/</span>
          <span>{snippets.length}</span>
        </div>
      </div>

      {/* Comments Panel */}
      {showComments && (
        <div className="comments-panel">
          <div className="comments-header">
            <h3>💬 Comments ({currentSnippet.comments})</h3>
            <button onClick={() => setShowComments(false)} className="close-btn">
              ✕
            </button>
          </div>

          <div className="comments-list">
            {(currentSnippet.commentsList || []).map(comment => (
              <div key={comment.id} className="comment">
                <span className="comment-author">{comment.username}</span>
                <p className="comment-text">{comment.text}</p>
                <span className="comment-time">
                  {new Date(comment.createdAt).toLocaleDateString()}
                </span>
              </div>
            ))}

            {(!currentSnippet.commentsList || currentSnippet.commentsList.length === 0) && (
              <p className="no-comments">Be the first to comment! 💬</p>
            )}
          </div>

          <div className="comment-input">
            <input
              type="text"
              placeholder="Add a comment..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleComment()}
            />
            <button onClick={handleComment} disabled={!commentText.trim()}>
              Post
            </button>
          </div>
        </div>
      )}

      {/* Create Button (FAB) */}
      <button 
        className="create-fab"
        onClick={() => setShowCreateModal(true)}
        title="Create your own snippet"
      >
        ➕
      </button>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="create-modal" onClick={(e) => e.stopPropagation()}>
            <h2>✨ Create Your Snippet</h2>
            <p>Feature coming soon! You'll be able to:</p>
            <ul>
              <li>✅ Write your own code snippets</li>
              <li>✅ Add challenges for others</li>
              <li>✅ Use hashtags to get discovered</li>
              <li>✅ Go viral in the coding community!</li>
            </ul>
            <button 
              onClick={() => setShowCreateModal(false)}
              className="close-modal-btn"
            >
              Got it!
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper function to format large numbers
function formatCount(count) {
  if (count >= 1000000) {
    return (count / 1000000).toFixed(1) + 'M';
  } else if (count >= 1000) {
    return (count / 1000).toFixed(1) + 'K';
  }
  return count.toString();
}

export default CodeTikTok;
